import React, { useState, useCallback, useEffect } from 'react';
import texasApi from '../../services/texasApi';

export const NoticeVacate: React.FC = () => {
  const [propertyId, setPropertyId] = useState<string>('');
  const [tenantId, setTenantId] = useState<string>('');
  const [leaseText, setLeaseText] = useState<string>('');
  const [properties, setProperties] = useState<Record<string, unknown>[]>([]);
  const [tenants, setTenants] = useState<Record<string, unknown>[]>([]);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProperties = useCallback(async () => {
    try {
      const props = await texasApi.noticeToVacate.getProperties();
      // Envelope-aware unwrap (F-144): the LP route returns { success, data: [...] },
      // legacy mocks/tests may return a bare array, and the properties service may
      // return { properties: [...] }. The previous Array.isArray-only check discarded
      // the envelope and left the dropdown permanently empty (human-reported).
      const body: any = (props as any)?.data ?? props;
      const list = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body?.properties)
        ? body.properties
        : [];
      setProperties(list);
    } catch {
      setError('Failed to load properties');
    }
  }, []);

  // Auto-load on mount (F-144): the dropdown must populate without a manual
  // click; the button remains as an explicit retry.
  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const loadTenants = useCallback(async (pid: string) => {
    if (!pid.trim()) return;
    try {
      const t = await texasApi.noticeToVacate.getTenants(pid);
      const tbody: any = (t as any)?.data ?? t;
      setTenants(Array.isArray(tbody) ? tbody : Array.isArray(tbody?.data) ? tbody.data : []);
    } catch {
      setTenants([]);
    }
  }, []);

  const handleSendDigitally = useCallback(async () => {
    if (!propertyId.trim() || !tenantId.trim() || !leaseText.trim()) return;
    setLoading(true);
    setError(null);
    setValidationResult(null);
    try {
      const caseId = `${propertyId}-${tenantId}`;
      const leaseDocument = leaseText.trim();
      const validateRes = await texasApi.noticeConsent.validate({ caseId, leaseDocument });
      const data = validateRes.data || validateRes;
      if (!data.valid) {
        const warning = data.warning || { code: 'DIGITAL_NOTICE_BLOCKED', message: 'Digital notice blocked' };
        setValidationResult({ ...data, blocked: true, warning });
        await texasApi.noticeConsent.recordPaper({ caseId, noticeType: 'NOTICE_TO_VACATE' });
      } else {
        await texasApi.noticeConsent.record({
          caseId,
          consent: data.consent,
          confidence: data.confidence,
          extractedTerms: data.extractedTerms,
          clauseRef: data.clauseRef,
          leaseVersion: '1.0',
          method: 'digital',
        });
        await texasApi.noticeToVacate.create({
          propertyId: propertyId.trim(),
          tenantId: tenantId.trim(),
          noticeDate: new Date().toISOString(),
          expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          daysRemaining: 30,
          served: false,
          status: 'active',
        });
        setValidationResult({ ...data, blocked: false });
      }
    } catch (err: any) {
      setError(err?.message || 'Notice operation failed');
    } finally {
      setLoading(false);
    }
  }, [propertyId, tenantId, leaseText]);

  return (
    <div data-testid="notice-vacate" className="texas-page-container">
      <h1>Notice to Vacate</h1>
      <p className="page-description">
        Digital Notice to Vacate with consent validation and paper fallback.
      </p>

      <section className="search-bar">
        <div className="field-group">
          <label htmlFor="property-select">Property</label>
          <select
            id="property-select"
            value={propertyId}
            onChange={(e) => { setPropertyId(e.target.value); loadTenants(e.target.value); }}
            data-testid="property-select"
          >
            <option value="">Select Property</option>
            {(properties as Record<string, string>[]).map((p, i) => (
              <option key={i} value={(p as Record<string, string>).id || String(p)}>
                {(p as Record<string, string>).name || String(p)}
              </option>
            ))}
          </select>
        </div>
        <div className="field-group">
          <label htmlFor="tenant-select">Tenant</label>
          <select
            id="tenant-select"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            data-testid="tenant-select"
          >
            <option value="">Select Tenant</option>
            {(tenants as Record<string, string>[]).map((t, i) => (
              <option key={i} value={(t as Record<string, string>).id || String(t)}>
                {(t as Record<string, string>).name || String(t)}
              </option>
            ))}
          </select>
        </div>
        <button type="button" onClick={loadProperties} disabled={loading} data-testid="load-properties-btn" title="Reload properties">
          Load Properties
        </button>
      </section>

      <section className="search-bar lease-document-field block w-full my-4">
        <label htmlFor="lease-input" className="block mb-1 font-medium" data-testid="lease-label">
          Lease document text
        </label>
        <textarea
          id="lease-input"
          value={leaseText}
          onChange={(e) => setLeaseText(e.target.value)}
          rows={6}
          data-testid="lease-input"
          className="lease-input w-full p-2 border border-gray-300 rounded"
          placeholder="Paste the lease text here (include the notice / electronic delivery clauses)"
        />
        <p className="helper-text mt-1 text-xs text-gray-600">
          Digital delivery requires an electronic-delivery consent clause in the lease,
          e.g. &quot;Tenant agrees to electronic delivery of notices&quot;.
        </p>
      </section>

      <section className="action-bar">
        <button
          type="button"
          onClick={handleSendDigitally}
          disabled={loading || !propertyId.trim() || !tenantId.trim() || !leaseText.trim()}
          data-testid="send-digitally-btn"
        >
          {loading ? 'Processing...' : 'Send Digitally'}
        </button>
      </section>

      {error && <div className="error" data-testid="error">{error}</div>}

      {validationResult && (
        <section data-testid="validation-result">
          {validationResult.blocked ? (
            <div
              className="red-warning"
              data-testid="digital-notice-blocked"
              data-type={validationResult.warning?.type || 'RED_WARNING'}
            >
              <strong>{validationResult.warning?.code || 'DIGITAL_NOTICE_BLOCKED'}</strong>
              <p>{validationResult.warning?.message || 'Digital notice to vacate blocked.'}</p>
              <p>Paper notice generated as fallback.</p>
            </div>
          ) : (
            <div className="success" data-testid="notice-granted">
              <strong>Notice to Vacate created</strong>
              <p>Consent granted. Notice recorded and created.</p>
              <p data-testid="confidence">
                Confidence: {typeof validationResult.confidence === 'number' ? `${validationResult.confidence}%` : 'n/a'}
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default NoticeVacate;